import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './TopicBoundariesDemoVoice.module.css';

type Who = 'user' | 'agent' | 'cue';

interface Line {
  who: Who;
  text: string;
}

interface Reply {
  say: string;
  answer: string;
  next: Step;
  note: string;
}

type Step = 'start' | 'groceries' | 'recipe' | 'done';

const REPLIES: Record<Step, Reply[]> = {
  start: [
    {
      say: 'Order more milk and eggs',
      answer:
        "I can't place orders - I handle your home devices. I can add milk and eggs to your shopping list, or remind you when you get home. Which one?",
      next: 'groceries',
      note: 'One-sentence decline, a clear boundary ("home devices"), then two things you can say right away.',
    },
    {
      say: 'Find me a pancake recipe',
      answer:
        "I can't look up recipes - I handle your home devices. I can preheat the oven, or set a kitchen timer. Which one?",
      next: 'recipe',
      note: 'Same boundary, different request. The alternatives are the closest things it can actually do.',
    },
    {
      say: 'Turn off the kitchen lights',
      answer: 'Kitchen lights are off.',
      next: 'start',
      note: 'In scope, so no boundary needed. Now try something it can\'t do.',
    },
  ],
  groceries: [
    {
      say: 'Add them to the list',
      answer: 'Done. Milk and eggs are on your shopping list.',
      next: 'done',
      note: 'The decline led straight to something useful. Nobody was told they asked the wrong question.',
    },
    {
      say: 'Remind me when I get home',
      answer: "Okay. I'll remind you about milk and eggs when you get home.",
      next: 'done',
      note: 'The user picked the second option. The need was met without the agent pretending it could shop.',
    },
  ],
  recipe: [
    {
      say: 'Preheat the oven',
      answer: 'Preheating the oven to 400 degrees. I\'ll chime when it\'s ready.',
      next: 'done',
      note: 'Out-of-scope question, in-scope result. The boundary is easy to remember for next time.',
    },
    {
      say: 'Set a timer for 20 minutes',
      answer: 'Timer set for 20 minutes.',
      next: 'done',
      note: 'The decline was short enough to sit through and ended with something to say.',
    },
  ],
  done: [],
};

const THINK_MS = 600;
const SPEAK_MS = 1800;

export function TopicBoundariesDemoVoice() {
  const [lines, setLines] = useState<Line[]>([{ who: 'cue', text: '(listening)' }]);
  const [step, setStep] = useState<Step>('start');
  const [speaking, setSpeaking] = useState(false);
  const [feedback, setFeedback] = useState('Say something to the home assistant. Try a request it can\'t do.');
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const list = timers.current;
    return () => list.forEach(clearTimeout);
  }, []);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const say = (r: Reply) => {
    setLines((l) => [...l.filter((x) => x.who !== 'cue'), { who: 'user', text: r.say }]);
    setSpeaking(true);
    setFeedback('The assistant is answering...');
    later(() => setLines((l) => [...l, { who: 'agent', text: r.answer }]), THINK_MS);
    later(() => {
      setSpeaking(false);
      setStep(r.next);
      setFeedback(r.note);
      if (r.next === 'done') setLines((l) => [...l, { who: 'cue', text: '(soft chime)' }]);
    }, THINK_MS + SPEAK_MS);
  };

  const options = REPLIES[step];

  return (
    <div className={kit.root}>
      <div className={styles.device}>
        <div className={`${kit.orb} ${speaking ? kit.orbActive : ''}`} aria-hidden="true" />
        <span className={kit.label}>{speaking ? 'Speaking' : step === 'done' ? 'Done' : 'Listening'}</span>
      </div>

      <div className={kit.transcript} aria-live="polite">
        {lines.map((l, i) =>
          l.who === 'cue' ? (
            <div className={kit.cue} key={i}>
              {l.text}
            </div>
          ) : (
            <div className={`${kit.voiceLine} ${l.who === 'agent' ? kit.voiceAgent : ''}`} key={i}>
              <span className={kit.voiceWho}>{l.who === 'agent' ? 'Home' : 'You'}</span>
              <p>{l.text}</p>
            </div>
          )
        )}
      </div>

      {options.length > 0 && (
        <div className={kit.row} aria-label="Things you can say">
          {options.map((r) => (
            <button
              type="button"
              key={r.say}
              className={`${kit.btn} ${step === 'start' ? '' : kit.btnPrimary}`}
              disabled={speaking}
              onClick={() => say(r)}
            >
              Say: "{r.say}"
            </button>
          ))}
        </div>
      )}

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
